import { useEventConfig } from '@/eventConfig/useEventConfig.ts';
import { translate } from '@/translation/translate.ts';
import { EditDataField } from '@/components/slides/DataSummary/components/EditDataField.tsx';
import { useSummaryFormField } from '@/components/slides/DataSummary/components/useSummaryFormField.ts';
import { EditSaveButtons } from '@/components/slides/DataSummary/components/EditSaveButtons.tsx';
import { addReqiredMessage } from '@/components/slides/DataSummary/util/addRequiredMessage.ts';
import { useForm } from '@/form/useForm.ts';
import { PhoneUtil } from '@/util/phoneUtils';

export const ContactSummaryFormUS = () => {
  const { onStartEdit, onCancelEdit, onSave, editMode, isValid } = useSummaryFormField({
    slideIds: ['email', 'phone'],
  });

  const { getFieldValue } = useForm();

  const { eventConfig } = useEventConfig();
  const s = eventConfig!.slides;
  const emailFields = s.email?.fields ?? [];
  const phoneFields = s.phone?.fields ?? [];

  const emailField = addReqiredMessage({
    field: emailFields.find(({ name }) => name === 'email'),
    message: s.dataSummary.requiredMessage!,
  });
  const emailLabel = translate(emailField?.label);

  const mobileField = addReqiredMessage({
    field: phoneFields.find(({ name }) => name === 'mobile'),
    message: s.dataSummary.requiredMessage!,
  });
  const mobileLabel = translate(mobileField?.label);
  const mobile = getFieldValue('mobile') ?? '';
  const decoratedMobile = PhoneUtil.formatPhoneNumber(mobile.toString(), 'national');

  const phoneField = addReqiredMessage({
    field: phoneFields.find(({ name }) => name === 'phone'),
    message: s.dataSummary.requiredMessage!,
  });
  const phoneLabel = translate(phoneField?.label);
  const phoneValue = getFieldValue('phone') ?? '';
  const decoratedPhoneNumber = PhoneUtil.formatPhoneNumber(phoneValue.toString(), 'national');

  const socialMediaField = addReqiredMessage({
    field: s.email.fields.find(({ name }) => String(name) === 'socialMedia'),
    message: s.dataSummary.requiredMessage!,
  });
  const socialMediaLabel = translate(socialMediaField?.label);
  const socialMediaValue =
    translate(socialMediaField?.options?.find(({ value }) => value === getFieldValue('socialMedia'))?.label) ||
    getFieldValue('socialMedia');
  const socialMediaConsent = getFieldValue('socialMedia');
  const isSocialMediaOptedIn =
    socialMediaConsent === true ||
    (typeof socialMediaConsent === 'string' && ['true', 'yes', 'y', '1'].includes(socialMediaConsent.toLowerCase()));

  const instagramField = s.email.fields.find(({ name }) => String(name) === 'instagram');
  const instagramLabel = translate(instagramField?.label);

  const tiktokField = s.email.fields.find(({ name }) => String(name) === 'tiktok');
  const tiktokLabel = translate(tiktokField?.label);

  const linkedinField = s.email.fields.find(({ name }) => String(name) === 'linkedin');
  const linkedinLabel = translate(linkedinField?.label);

  const facebookField = emailFields.find(({ name }) => String(name) === 'facebook');
  const facebookLabel = translate(facebookField?.label);

  const urlField = emailFields.find(({ name }) => String(name) === 'url');
  const urlLabel = translate(urlField?.label);

  return (
    <div className="summary-card" data-cy={'summary-form-contact'}>
      <h2>{translate(s.dataSummary.titles?.contact)}</h2>
      <EditSaveButtons
        editMode={editMode}
        onSave={onSave}
        onStartEdit={onStartEdit}
        onCancelEdit={onCancelEdit}
        isValid={isValid}
      />

      <div className="data">
        <div className={'label'}>{emailLabel}</div>
        <div className={'value'}>
          {getFieldValue('email')}
          {
            <div style={{ display: 'none' }}>
              <EditDataField field={emailField!} slideId={'email'} />
            </div>
          }
        </div>

        <div className={'label'}>{mobileLabel}</div>
        <div className={'value'}>
          {!editMode && decoratedMobile}
          {editMode && mobileField && <EditDataField field={mobileField} slideId={'phone'} />}
        </div>

        <div className={'label'}>{phoneLabel}</div>
        <div className={'value'}>
          {!editMode && decoratedPhoneNumber}
          {editMode && phoneField && <EditDataField field={phoneField} slideId={'phone'} />}
        </div>

        <div className={'label'}>{socialMediaLabel}</div>
        <div className={'value'}>
          {!editMode && socialMediaValue}
          {editMode && socialMediaField && <EditDataField field={socialMediaField} slideId={'email'} />}
        </div>

        {isSocialMediaOptedIn && (
          <>
            <div className={'label'}>{instagramLabel}</div>
            <div className={'value'}>
              {!editMode && getFieldValue('instagram')}
              {editMode && instagramField && <EditDataField field={instagramField} slideId={'email'} />}
            </div>

            <div className={'label'}>{facebookLabel}</div>
            <div className={'value'}>
              {!editMode && getFieldValue('facebook')}
              {editMode && facebookField && <EditDataField field={facebookField} slideId={'email'} />}
            </div>

            <div className={'label'}>{linkedinLabel}</div>
            <div className={'value'}>
              {!editMode && getFieldValue('linkedin')}
              {editMode && linkedinField && <EditDataField field={linkedinField} slideId={'email'} />}
            </div>

            <div className={'label'}>{tiktokLabel}</div>
            <div className={'value'}>
              {!editMode && getFieldValue('tiktok')}
              {editMode && tiktokField && <EditDataField field={tiktokField} slideId={'email'} />}
            </div>

            <div className={'label'}>{urlLabel}</div>
            <div className={'value'}>
              {!editMode && getFieldValue('url')}
              {editMode && urlField && <EditDataField field={urlField} slideId={'email'} />}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
